// #TODO Interface

// Angular
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
// Rxjs
import { of as observableOf } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
// Afidium
import { ResourceError, ResourceStatus } from "libraries/service/resource/ResourceError";
import { ViewsService } from 'libraries/view/views.service';
import { AppRootService } from 'app/shared/services/app-root/app-root-service';
import { ISearchStatus } from "../../shared/interfaces/ISearchStatus";
import { ProcessingResultsContainerContexts } from "./components/processing-result-container/processing-result-container.component";
import { FIELD_LABELS } from "../../shared/config/processing-wording";
import { ProcessingSearchParameters } from '../../shared/models/ProcessingSearchParameters';
import { ISummary } from 'libraries/component/search-summary/ISummary';
import { ProcessingSummaryService } from './processing-summary.service';
import { SashaBreadcrumbService } from "../../shared/components/breadcrumb/sasha-breadcrumb.service";

import { Yii } from "libraries/interfaces/Yii";
import { SashaPageComponent } from '../sasha-page.component';
import { IApiProcessingSearchResponse } from 'app_sasha/shared/resources/processings/IApiProcessingSearchResponse';
import { ProcessingResourceService } from 'app_sasha/shared/resources/processings/processingResource.service';
import { TitleService } from 'app_sasha/shared/services/title/title.service';
import { ICompleteNumberLabels, LabelService } from 'libraries/service/label/label.service';
import { ErrorTracingService } from "libraries/service/error-tracing/error-tracing.service";

@Component({
    selector: 'processing-search-result',
    template: `
        <div class="app-wrapper app-sasha">
            <sasha-breadcrumb></sasha-breadcrumb>
            <ng-container *ngIf="showForm; then formTemplate; else summaryTemplate"></ng-container>
            <ng-template #summaryTemplate>
                <search-summary
                        [fields]="summary"
                        (onRemove)="onSummaryChange($event)"
                        (onModifySearch)="onModifySearch()"></search-summary>
            </ng-template>
            <ng-template #formTemplate>
                <processing-search-form
                        [searchParameters]="searchProcessingParams"
                        (onSubmit)="onSubmit($event)"
                        (onReset)="onReset()">
                </processing-search-form>
            </ng-template>
            <processing-results-container [context]="resultContainerContext"
                                          [searchStatus]="searchStatus"
                                          [recordset]="response && response.recordset ? response.recordset : {}"
                                          [processings]="response && response.processing ? response.processing : []">
                <ng-container *ngIf="response && response.processing && response.processing.length > 0"
                              class="processing-results-footer">
                    <pagination [listLength]="response.processing.length"
                                [recordset]="response.recordset"
                                [pageSizeOptions]="possibleSizes"
                                (onPageChange)="onPageChange($event)"></pagination>
                </ng-container>
            </processing-results-container>
        </div>`
})
export class SearchResultComponent extends SashaPageComponent implements OnInit {

    public searchStatus: ISearchStatus;

    public response: IApiProcessingSearchResponse;
    public searchProcessingParams: ProcessingSearchParameters;
    public summary: ISummary[];
    public possibleSizes: number[] = null;
    public showForm: boolean = false;

    public resultContainerContext: ProcessingResultsContainerContexts =
        ProcessingResultsContainerContexts.SEARCH_RESULT;

    private readonly titleLabels: ICompleteNumberLabels = {
        one: Yii.t("wui-sasha-js", "Un traitement"),
        none: Yii.t("wui-sasha-js", "Aucun traitement"),
        default: Yii.t("wui-sasha-js", "%placeholder% traitements"),
    };

    constructor(
        private readonly route: ActivatedRoute,
        private readonly processingResource: ProcessingResourceService,
        private readonly router: Router,
        private readonly summaryService: ProcessingSummaryService,
        protected views: ViewsService,
        appRootService: AppRootService,
        private readonly breadcrumbService: SashaBreadcrumbService,
        private readonly titleService: TitleService,
        private readonly labelService: LabelService,
        private readonly errorTracingService: ErrorTracingService,
    ) {
        super(views, appRootService);
    }

    public ngOnInit(): void {
        super.ngOnInit();

        this.searchStatus = {
            isLoading: false,
            isError: false
        };

        this.route
            .queryParams
            .pipe(
                tap((queryParams) => {
                    this.showForm = Boolean(queryParams.form);
                    this.titleService.setTitle(Yii.t("wui-sasha-js", "Recherche de traitements..."));
                }),
                map((params: any) => ProcessingSearchParameters.fromRouteParams(params)),
                tap((params: ProcessingSearchParameters) => {
                    this.searchStatus = this.getSearchStatus({isLoading: true, isError: false, isWarning: false});
                    this.response = undefined;
                    this.searchProcessingParams = params;
                    this.summary = this.summaryService.createSummary(params, FIELD_LABELS);
                    this.breadcrumbService.setProcessingSearchResult(params);
                }),
                switchMap((searchParameters: ProcessingSearchParameters) => {
                    return this.processingResource.search(searchParameters)
                        .pipe(
                            map((response: IApiProcessingSearchResponse) => {
                                this.response = response;

                                this.titleService.setTitle(
                                    this.labelService.completeNumber(this.response.recordset.total, this.titleLabels),
                                    Yii.t("wui-sasha-js", "Recherche de traitements"),
                                );

                                this.possibleSizes = [20, 50, 100]
                                    .filter((value: number) => value <= response.recordset.total);

                                this.searchStatus = this.getSearchStatus({isLoading: false});

                                this.possibleSizes = this.possibleSizes.length ? this.possibleSizes : null;

                                if (this.response.processing && this.response.processing.length === 0) {
                                    this.searchStatus = this.getSearchStatus({
                                        isLoading: false,
                                        isWarning: true,
                                        isError: false,
                                        errorMessage: Yii.t('wui-sasha-js', 'Aucun résultat pour votre recherche'),
                                    });
                                }
                            }),
                            catchError((error: ResourceError) => {
                                if (error.status === ResourceStatus.NotAuthorized) {
                                    this.router.navigate(['/login'])
                                        .catch((reason: boolean) => {
                                            this.errorTracingService.captureException(reason);
                                        });
                                }
                                this.searchStatus = this.getSearchStatus({
                                    isLoading: false,
                                    isWarning: false,
                                    isError: true,
                                    errorMessage: error.message,
                                });
                                return observableOf(null);
                            })
                        );
                }),
            )
            .subscribe(
                () => {
                    //
                },
                (error: Error) => {
                    this.errorTracingService.captureException(error);
                    this.searchStatus = this.getSearchStatus({
                        isError: true,
                        isLoading: false,
                        errorMessage: error.message
                    });
                },
                () => {
                    this.searchStatus = this.getSearchStatus({isLoading: false});
                });
    }

    public onPageChange(event: { first: number, rows: number }): void {
        const newSearchParameters: ProcessingSearchParameters = this.searchProcessingParams.clone();
        newSearchParameters.nbrecord = event.rows;
        newSearchParameters.first = event.first + 1;

        this.router.navigate([], {queryParams: newSearchParameters.convertToUrlParameters()})
            .catch((reason: boolean) => {
                this.errorTracingService.captureException(reason);
            });
    }

    public onSummaryChange(values: ISummary[]): void {
        const removed: string[] = values.map((value: ISummary) => value.field);
        const newSearchParameters: ProcessingSearchParameters = this.searchProcessingParams.clone().clearValues(removed);
        newSearchParameters.first = 1;
        const queryParams = newSearchParameters.convertToUrlParameters();

        this.router.navigate([], {queryParams})
            .catch((reason: boolean) => {
                this.errorTracingService.captureException(reason);
            });
    }

    public onModifySearch(): void {
        // const searchParameters = this.searchProcessingParams;
        const params = this.searchProcessingParams.convertToUrlParameters();

        this.router.navigate(
            ['.'],
            {
                relativeTo: this.route,
                queryParams: {...params, form: true},
            }
        ).catch((reason: boolean) => {
            this.errorTracingService.captureException(reason);
        });
    }

    public onSubmit(searchParameters: ProcessingSearchParameters): void {
        const params = searchParameters.convertToUrlParameters();
        // first refresh to the route with the open form
        this.router.navigate(
            ['.'],
            {
                relativeTo: this.route,
                queryParams: {...params, form: true},
            }
        ).then(// then change route to hide the form
            async () => this.router.navigate(
                ['.'],
                {
                    relativeTo: this.route,
                    queryParams: params,
                }
            )
        ).catch((reason: boolean) => {
            this.errorTracingService.captureException(reason);
        });
    }

    public onReset(): void {
        const searchParameters: ProcessingSearchParameters = new ProcessingSearchParameters();
        const queryParams = searchParameters.convertToUrlParameters();

        this.router.navigate(
            ['/sasha/processing/search'],
            {
                queryParams,
            }
        ).catch((reason: boolean) => {
            this.errorTracingService.captureException(reason);
        });
    }

    private getSearchStatus(newStatus: ISearchStatus): ISearchStatus {
        return {
            ...this.searchStatus,
            ...newStatus
        };
    }
}
